import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  TrendingUp, 
  PiggyBank, 
  Target,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth.context';
import { ACCOUNT_DELETED_NOTICE } from '@/lib/account-deletion';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';
import { getLoginErrorMessage } from '@/lib/auth-errors';
import {
  ACCOUNT_CLOSED_PARAM,
  ACCOUNT_CLOSED_VALUE,
  ADMIN_IMPERSONATION_TOKEN_PARAM,
  ADMIN_RETURN_TO_PARAM,
} from '@/lib/auth-routes';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: authLogin, loginWithAdminImpersonation } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  // Mensagem de erro exibida abaixo do campo de senha (mesma do toast).
  const [loginError, setLoginError] = useState('');
  // "Manter Conectado" — opt-in ativo, começa desmarcado (RN).
  const [rememberMe, setRememberMe] = useState(false);
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const passwordInputRef = useRef<HTMLInputElement | null>(null);
  const impersonationHandledRef = useRef(false);
  const contaEncerrada = searchParams.get(ACCOUNT_CLOSED_PARAM) === ACCOUNT_CLOSED_VALUE;

  useEffect(() => {
    const token = searchParams.get(ADMIN_IMPERSONATION_TOKEN_PARAM);
    const adminReturnUrl = searchParams.get(ADMIN_RETURN_TO_PARAM);

    if (!token || impersonationHandledRef.current) {
      return;
    }

    impersonationHandledRef.current = true;
    setIsLoading(true);

    loginWithAdminImpersonation(token, adminReturnUrl)
      .catch(() => {
        // A mensagem de erro já é exibida pelo AuthContext.
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [searchParams, loginWithAdminImpersonation]);

  const handleLogin = async () => {
    // Fallback: em alguns ambientes de automação, o `onChange` pode não disparar.
    // Ler diretamente do DOM garante que o login funcione quando o usuário já digitou nos campos.
    const email = (emailInputRef.current?.value ?? loginData.email).trim();
    const password = passwordInputRef.current?.value ?? loginData.password;

    if (!email || !password) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos",
        variant: "destructive",
      });
      return;
    }

    setLoginError('');
    setIsLoading(true);
    try {
      const result = await authLogin(email, password, rememberMe);

      // Check if 2FA is required
      if (result.requires2FA) {
        navigate('/two-factor', {
          state: {
            tempToken: result.tempToken,
            email: result.email ?? email,
            rememberMe,
          }
        });
      }
    } catch (error: unknown) {
      console.error('Erro no login:', error);
      // Espelha a mensagem do toast abaixo do campo de senha, para que o
      // usuário leia com clareza o motivo de não ter conseguido entrar.
      setLoginError(getLoginErrorMessage(error).description);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: TrendingUp,
      title: 'Dashboard Inteligente',
      description: 'Visualize suas finanças com gráficos e KPIs em tempo real.',
    },
    {
      icon: PiggyBank,
      title: 'Controle de Gastos',
      description: 'Acompanhe receitas e despesas de forma simples e organizada.',
    },
    {
      icon: Target,
      title: 'Despesas Futuras',
      description: 'Defina objetivos e acompanhe seu progresso até alcançá-los.',
    },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 brand-light-surface order-1 lg:order-1">
        <div className="w-full max-w-md space-y-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo-librattum.png"
              alt="Librattum"
              className="h-10 mx-auto"
            />
          </div>

          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              Bem-vindo de volta
            </h2>
            <p className="mt-2 text-gray-600">
              Entre na sua conta para continuar
            </p>
          </div>

          {contaEncerrada && (
            // Chegada depois do "Excluir conta" (chamado 7116).
            <output className="block rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              {ACCOUNT_DELETED_NOTICE}
            </output>
          )}

          {/* Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700">
                  E-mail
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                    value={loginData.email}
                    onChange={(e) => {
                      if (loginError) setLoginError('');
                      setLoginData(prev => ({ ...prev, email: e.target.value }));
                    }}
                    ref={emailInputRef}
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-gray-700">
                    Senha
                  </Label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm text-[#F5B322] hover:underline"
                  >
                    Esqueceu a senha?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    className="pl-11 pr-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                    value={loginData.password}
                    onChange={(e) => {
                      if (loginError) setLoginError('');
                      setLoginData(prev => ({ ...prev, password: e.target.value }));
                    }}
                    ref={passwordInputRef}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {loginError && (
                  <p role="alert" className="text-sm text-red-600">
                    {loginError}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
                disabled={isLoading}
                className="data-[state=checked]:bg-[#F5B322] data-[state=checked]:border-[#F5B322] data-[state=checked]:text-[#2F2117]"
              />
              <Label htmlFor="remember-me" className="text-sm text-[#F5B322] cursor-pointer">
                Manter conectado
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
              disabled={isLoading}
              onClick={(e) => {
                // Garante funcionamento mesmo em ambientes que não disparam o submit padrão do form
                e.preventDefault();
                handleLogin();
              }}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-[#2F2117]/30 border-t-[#2F2117] rounded-full animate-spin" />
                  Entrando...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  Entrar
                  <ArrowRight className="h-4 w-4" />
                </div>
              )}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-50 text-gray-500">ou</span>
            </div>
          </div>

          {/* Register Link */}
          <div className="text-center">
            <p className="text-gray-600">
              Ainda não tem uma conta?{' '}
              <Link 
                to="/register" 
                className="font-semibold text-[#F5B322] hover:underline"
              >
                Criar conta grátis
              </Link>
            </p>
          </div>

          {/* Trust Badges */}
          <div className="flex items-center justify-center gap-6 pt-4 text-sm text-gray-500">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[#F5B322]" />
              Dados seguros
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-[#F5B322]" />
              100% gratuito para começar
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Hero */}
      <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between relative overflow-hidden order-2" style={{ background: BRAND_HERO_GRADIENT }}>
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        {/* Logo */}
        <div className="relative z-10">
          <img
            src="/logoSidebar.png"
            alt="Librattum"
            className="h-10"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-white leading-tight">
              Organize suas finanças<br />
              <span className="text-[#F5B322]">de forma inteligente</span>
            </h1>
            <p className="text-gray-300 text-lg max-w-md">
              Planeje, controle e alcance seus objetivos financeiros com uma plataforma moderna e intuitiva.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-4 p-4 rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm"
              >
                <div className="p-2 rounded-lg bg-[#F5B322]/20">
                  <feature.icon className="h-5 w-5 text-[#F5B322]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{feature.title}</h3>
                  <p className="text-sm text-gray-300">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-gray-400 text-sm">
          © {new Date().getFullYear()} Librattum. Todos os direitos reservados.
        </div>
      </div>
    </div>
  );
}
